import { useState, useCallback, useEffect } from "react";
import { 
  UPLOAD_WORKER_URL, 
  isMedia, 
  isVideo, 
  generateGuidFilename 
} from "../utils/utils-file";
import Toast from "../components/MediaUploader/toast-component";
import LoginModal from "../components/Common/LoginModal";
import CameraSelect from "../components/MediaUploader/camera-select-component";
import CustomTagInput from "../components/MediaUploader/custom-tag-input-component";
import FilePicker from "../components/MediaUploader/file-picker-component";
import FileSummary from "../components/MediaUploader/file-summary-component";
import FilesGrid from "../components/MediaUploader/files-grid-component";
import UploadControls from "../components/MediaUploader/upload-controls-component";
import "../styles/MediaUploader.css";

export default function MediaUploaderPage() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authGuid, setAuthGuid] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  
  const [camera, setCamera] = useState("Staff Upload");
  const [customTag, setCustomTag] = useState("");

  /* ───────────  AUTH LOGIC  ─────────── */
  useEffect(() => {
    const checkAuth = async () => {
      const storedAuth = localStorage.getItem("uploadAuth");
      if (storedAuth) {
        const { guid, timestamp } = JSON.parse(storedAuth);
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          setAuthGuid(guid);
          return;
        }
      }
      setShowPasswordModal(true);
    };
    checkAuth();
  }, []);

  const handleAuthSubmit = async (password) => {
    setAuthError("");
    setAuthLoading(true);
    
    try {
      const response = await fetch("https://dat-photo-auth.t9e2i3kal.workers.dev/upload_auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      
      if (!response.ok) {
        throw new Error("Invalid password");
      }
      
      const contentType = response.headers.get("content-type");
      let guid;
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        guid = data.guid;
      } else {
        guid = await response.text();
      }
      
      if (!guid) {
        throw new Error("No GUID received");
      }
      
      setAuthGuid(guid);
      localStorage.setItem("uploadAuth", JSON.stringify({
        guid,
        timestamp: Date.now()
      }));
      setShowPasswordModal(false);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  /* ───────────  FILE MANAGEMENT  ─────────── */
  const addFiles = useCallback((incoming) => {
    const valid = incoming.filter(isMedia);
    if (valid.length !== incoming.length) {
      alert(`${incoming.length - valid.length} skipped (images/videos only).`);
    }
    setFiles((prev) => [
      ...prev,
      ...valid.map((file, idx) => ({
        id: Date.now() + idx,
        file,
        name: file.name,
        guidName: generateGuidFilename(file.name),
        size: file.size,
        type: isVideo(file) ? "video" : "image",
        status: "pending",
        preview: URL.createObjectURL(file),
      })),
    ]);
  }, []);

  const removeFile = (id) =>
    setFiles((prev) => {
      const doomed = prev.find((f) => f.id === id);
      if (doomed?.preview) URL.revokeObjectURL(doomed.preview);
      return prev.filter((f) => f.id !== id);
    });

  const updateFile = (id, patch) =>
    setFiles((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const clearAll = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
    setUploadProgress(0);
    setShowToast(false);
  };

  /* ───────────  UPLOAD LOGIC  ─────────── */
  const uploadFile = async (fileObj) => {
    if (!authGuid) {
      throw new Error("Not authenticated");
    }
    
    updateFile(fileObj.id, { status: "uploading" });
    
    try {
      const fd = new FormData();
      fd.append("file", fileObj.file);
      fd.append("filename", fileObj.guidName);
      fd.append("metadata", JSON.stringify({
        camera: customTag || camera,
        datetime: new Date().toISOString(),
      }));

      const res = await fetch(`${UPLOAD_WORKER_URL}/upload`, {
        method: "POST",
        headers: { "X-Auth-Guid": authGuid },
        body: fd,
      });
      
      const out = await res.json();
      if (!out.success) throw new Error(out.error || "Upload failed");
      
      updateFile(fileObj.id, { status: "success" });
    } catch (err) {
      updateFile(fileObj.id, { status: "error" });
    }
  };

  const uploadAll = async () => {
    setUploading(true);
    setUploadProgress(0);
    const pending = files.filter((f) => f.status === "pending");
    
    if (pending.length === 0) {
      setUploading(false);
      return;
    }

    // Create progress tracking for each file
    const fileProgress = new Map();
    pending.forEach(file => fileProgress.set(file.id, 0));

    const updateOverallProgress = () => {
      const totalProgress = Array.from(fileProgress.values()).reduce((sum, progress) => sum + progress, 0);
      const averageProgress = totalProgress / pending.length;
      setUploadProgress(averageProgress);
    };

    // Simulate gradual progress during upload
    const simulateProgress = (fileId, duration = 6000) => {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * 85, 85); // Cap at 85% until completion
        fileProgress.set(fileId, progress);
        updateOverallProgress();
        
        if (elapsed >= duration) {
          clearInterval(interval);
        }
      }, 150);
      
      return interval;
    };

    await Promise.all(pending.map(async (file) => {
      const progressInterval = simulateProgress(file.id);
      
      try {
        await uploadFile(file);
        clearInterval(progressInterval);
        fileProgress.set(file.id, 100);
        updateOverallProgress();
      } catch (err) {
        console.error("Upload failed:", err);
        clearInterval(progressInterval);
        fileProgress.set(file.id, 0);
        updateOverallProgress();
      }
    }));

    // Ensure we reach 100%
    setUploadProgress(100);
    setTimeout(() => {
      setUploading(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }, 500);
  };

  /* ───────────  RENDER  ─────────── */
  return (
    <div className="media-page">
      <LoginModal
        show={showPasswordModal}
        onSubmit={handleAuthSubmit}
        error={authError}
        loading={authLoading}
        title="Enter Password"
      />

      <Toast
        show={showToast}
        message="All files uploaded!"
        onClose={() => setShowToast(false)}
      />

      <div className="uploader-card">
        <header className="uploader-header">
          <h1>Upload Media</h1>
          <p>Photos and videos supported</p>
        </header>

        <CameraSelect value={camera} onChange={setCamera} />
        <CustomTagInput value={customTag} onChange={setCustomTag} />
        <FilePicker onFilesAdded={addFiles} disabled={uploading} />

        {files.length > 0 && (
          <>
            <FileSummary files={files} />
            <FilesGrid 
              files={files}
              onRemoveFile={removeFile}
              uploading={uploading}
            />
            <UploadControls
              files={files}
              uploading={uploading}
              uploadProgress={uploadProgress}
              onUpload={uploadAll}
              onClear={clearAll}
            />
          </>
        )}
      </div>
    </div>
  );
}
